import { Injectable } from '@angular/core';
import { SupabaseCore } from './supabase.core';

@Injectable({
  providedIn: 'root',
})
export class SupabaseStorage {
  constructor(private supabaseCore: SupabaseCore) {}

  bucket(name: string) {
    return this.supabaseCore.client.storage.from(name);
  }

  async upload(bucket: string, path: string, file: File, options?: any): Promise<any> {
    return await this.supabaseCore.client.storage.from(bucket).upload(path, file, options);
  }

  async download(bucket: string, path: string): Promise<Blob> {
    const { data, error } = await this.supabaseCore.client.storage.from(bucket).download(path);

    if (error) throw error;
    return data;
  }

  getPublicUrl(bucket: string, path: string): string {
    const { data } = this.supabaseCore.client.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }

  async remove(bucket: string, paths: string[]) {
    return await this.supabaseCore.client.storage.from(bucket).remove(paths);
  }

  async list(bucket: string, path?: string, options?: any): Promise<any[]> {
    const { data, error } = await this.supabaseCore.client.storage.from(bucket).list(path, options);

    if (error) throw error;
    return data;
  }

  /**
   * Crea una URL firmada para acceso temporal a un archivo privado
   * @param bucket - Nombre del bucket
   * @param path - Ruta del archivo
   * @param expiresIn - Tiempo de expiración en segundos (por defecto 1 hora)
   * @returns Promesa con la URL firmada y tiempo de expiración
   */
  async createSignedUrl(
    bucket: string,
    path: string,
    expiresIn: number = 3600
  ): Promise<{ data: { signedUrl: string }; error: any }> {
    try {
      const { data, error } = await this.supabaseCore.client.storage
        .from(bucket)
        .createSignedUrl(path, expiresIn);

      if (error) {
        return {
          data: { signedUrl: '' },
          error,
        };
      }

      return {
        data: { signedUrl: data.signedUrl },
        error: null,
      };
    } catch (error) {
      return {
        data: { signedUrl: '' },
        error: error instanceof Error ? error : new Error('Error desconocido'),
      };
    }
  }

  /**
   * Crea múltiples URLs firmadas para varios archivos
   * @param bucket - Nombre del bucket
   * @param paths - Array de rutas de archivos
   * @param expiresIn - Tiempo de expiración en segundos
   * @returns Promesa con un array de URLs firmadas
   */
  async createSignedUrls(
    bucket: string,
    paths: string[],
    expiresIn: number = 3600
  ): Promise<{ data: Array<{ path: string; signedUrl: string }>; error: any }> {
    try {
      const { data, error } = await this.supabaseCore.client.storage
        .from(bucket)
        .createSignedUrls(paths, expiresIn);

      if (error) {
        return {
          data: [],
          error,
        };
      }

      return {
        data:
          data?.map((item) => ({
            path: item.path || '',
            signedUrl: item.signedUrl,
          })) || [],
        error: null,
      };
    } catch (error) {
      return {
        data: [],
        error: error instanceof Error ? error : new Error('Error desconocido'),
      };
    }
  }
}
